import * as _ from 'lodash';
import {createAction, Store} from '@ngrx/store';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {Injectable, NgZone} from '@angular/core';
import {map, mergeMap} from 'rxjs/operators';

import {OnMessage, VaBatchStatusUpdate, VaTimecardStatusUpdate} from '../core/actions/notification.action';
import {Notifications, NotificationType} from '../../../common/notification.model';
import {selectTimecardId} from '../timecard/selectors/timecard-entry.selector';
import {UpdateTimecardVaExceptionsStatus} from '../timecard/actions/timecard-entry-ui.action';
import {BatchDetailsVaStatusPayload} from '../paymaster/actions/paymaster-batch-details.action';
import {getState} from '../shared/utils';
import {NoOpAction} from '../actions/no-op.action';
import {NotificationEventSourceService} from '../services/notification-event-source.service';

/**
 * Primary tab = original tab where the user logs in, which maintains the SSE connection open until the user logs out.
 * Non-primary tabs = listens to primary tab for SSE messages using BroadcastChannel API.
 */
@Injectable()
export class BroadcastSseNotificationEffects {
  // This will broadcast SSE messages from primary tab (which maintains SSE connection) to non-primary tabs
  private _sseBroadcastChannel = new BroadcastChannel('sse-notifications');

  constructor(
    private actions: Actions,
    private _store: Store,
    private _notificationService: NotificationEventSourceService,
    private _ngZone: NgZone) {
    // Subscribe to BroadcastChannel API messages, if the current tab is not the primary tab
    this._sseBroadcastChannel.onmessage = (event) => {
      if (!this._notificationService.isSsePrimaryTab()) {
        // Wrap in NgZone so Angular triggers change detection
        this._ngZone.run(() => {
          this._store.dispatch(OnMessage(event.data as Notifications));
        });
      }
    };
  }

  // Map NotificationType to corresponding action creators
  private _notificationActionMap: Record<
    NotificationType,
    ReturnType<typeof createAction>
  > = {
    [NotificationType.VA_TIMECARD_STATUS_UPDATE]: VaTimecardStatusUpdate,
    [NotificationType.VA_BATCH_STATUS_UPDATE]: VaBatchStatusUpdate,
  };

  /**
   * Dispatch actions based on SSE notification type
   */
  dispatchActionsBasedOnNotificationType = createEffect(() =>
    this.actions.pipe(
      ofType(OnMessage),
      mergeMap(({ message }) => {
        // Broadcast SSE message to non-primary tabs from primary tab
        if (this._notificationService.isSsePrimaryTab()) {
          this._sseBroadcastChannel.postMessage(message);
        }
        const notificationType = _inferNotificationType(message);
        const dispatchAction = this._notificationActionMap[notificationType];
        return dispatchAction
          ? [dispatchAction(message)]
          : [];
      })
    )
  );

  /**
   * Checks if notification payload's timecardId matches currently opened timecard's id
   * If yes, update the Va exception status of the opened timecard, else ignore the message
   */
  vaTimecardStatusEffect = createEffect(() =>
    this.actions.pipe(
      ofType(VaTimecardStatusUpdate),
      map(({ message }) => {
        const openedTimecardId = getState(this._store, selectTimecardId);
        return message.timecardId === openedTimecardId
          ? UpdateTimecardVaExceptionsStatus(message.openExceptions)
          : NoOpAction();
      }),
    )
  );

  /**
   * Dispatches Va payload to Paymaster module
   */
  vaBatchStatusEffect = createEffect(() =>
    this.actions.pipe(
      ofType(VaBatchStatusUpdate),
      map(({message}) => BatchDetailsVaStatusPayload(message))
    )
  );
}

/**
 * Infers notification type from SSE message so that we can dispatch the corresponding action
 */
const _inferNotificationType = (message: Notifications): NotificationType | null => {
  if (_.has(message, 'openExceptions') && _.has(message, 'timecardId')) {
    return NotificationType.VA_TIMECARD_STATUS_UPDATE;
  }
  if (_.has(message, 'openExceptions') && _.has(message, 'batchId')) {
    return NotificationType.VA_BATCH_STATUS_UPDATE;
  }
  // Add more type checks here for other notifications
  return null;
};

